import { ExperimentsAPI } from '@wix/editorx-core-api';

export const COMPONENT_SPEC_FILTERING_SPEC =
  'specs.editor-platform.EnableComponentSpecFiltering' as const;
export const RUNTIME_MANIFEST_LABELS_SPEC =
  'enableRuntimeManifestUxData' as const;

const getUrlParams = () => {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return null;
  }

  return new URLSearchParams(window.location.search);
};

export class ManifestServiceFlags {
  static create(experimentsAPI: ExperimentsAPI) {
    const params = getUrlParams();

    const isPlatformCacheDisabled = params?.has('disablePlatformCaches');

    const isVirtualManifestBuildCacheDisabled = isPlatformCacheDisabled
      ? isPlatformCacheDisabled
      : params?.has('disablePlatformVirtualManifestBuildCache');

    const isVirtualManifestAPICacheDisabled = isPlatformCacheDisabled
      ? isPlatformCacheDisabled
      : params?.has('disablePlatformVirtualManifestAPICache');

    const isManifestDynamicOverridesCacheDisabled = isPlatformCacheDisabled
      ? isPlatformCacheDisabled
      : params?.has('disableManifestDynamicOverridesCache');
    const isPersistentResolvedManifestCacheDisabled = isPlatformCacheDisabled
      ? isPlatformCacheDisabled
      : params?.has('disablePlatformPersistentManifestCache');

    const isRuntimeManifestResolutionEnabled = experimentsAPI.enabled(
      'specs.editor-platform.EnableRuntimeManifestResolution',
    );
    const isLoadManifestWithoutDuplicationsEnabled = experimentsAPI.enabled(
      'specs.editor-platform.loadManifestWithoutDuplications',
    );
    const isRuntimeManifestOverridesResolutionEnabled = experimentsAPI.enabled(
      'specs.editor-platform.EnableRuntimeManifestOverridesResolution',
    );

    const isPersistentResolvedManifestCacheEnabled =
      isRuntimeManifestResolutionEnabled &&
      experimentsAPI.enabled(
        'specs.editor-platform.EnablePersistentManifestCache',
      ) &&
      !isPersistentResolvedManifestCacheDisabled;

    const shouldMergeSlotManifests = experimentsAPI.enabled('actionable_slots');

    const shouldUseBuilderSlotsAPI = experimentsAPI.enabled(
      'specs.editorplatform.BuilderComponentsSlotApiMigration',
    );

    const isComponentSpecFilteringEnabled = experimentsAPI.enabled(
      COMPONENT_SPEC_FILTERING_SPEC,
    );
    const isRuntimeManifestLabelsEnabled = experimentsAPI.enabled(
      RUNTIME_MANIFEST_LABELS_SPEC,
    );

    return new ManifestServiceFlags(
      {
        isPlatformCacheDisabled: !!isPlatformCacheDisabled,
        isVirtualManifestAPICacheEnabled: !isVirtualManifestAPICacheDisabled,
        isVirtualManifestBuildCacheEnabled:
          !isVirtualManifestBuildCacheDisabled,
        isManifestDynamicOverridesCacheEnabled:
          !isManifestDynamicOverridesCacheDisabled,
        shouldMergeSlotManifests,
        shouldUseBuilderSlotsAPI,
        isRuntimeManifestResolutionEnabled,
        isPersistentResolvedManifestCacheEnabled,
        isLoadManifestWithoutDuplicationsEnabled,
        isRuntimeManifestOverridesResolutionEnabled,
        isComponentSpecFilteringEnabled,
        isRuntimeManifestLabelsEnabled,
      },
      experimentsAPI,
    );
  }

  public readonly debug: boolean = false;

  #experimentsAPI: ExperimentsAPI | undefined;

  constructor(
    private readonly flags: {
      isPlatformCacheDisabled?: boolean;
      isManifestDynamicOverridesCacheEnabled?: boolean;
      isVirtualManifestAPICacheEnabled?: boolean;
      isVirtualManifestBuildCacheEnabled?: boolean;
      shouldMergeSlotManifests?: boolean;
      shouldUseBuilderSlotsAPI?: boolean;
      isRuntimeManifestResolutionEnabled?: boolean;
      isPersistentResolvedManifestCacheEnabled?: boolean;
      isLoadManifestWithoutDuplicationsEnabled?: boolean;
      isRuntimeManifestOverridesResolutionEnabled?: boolean;
      isComponentSpecFilteringEnabled?: boolean;
      isRuntimeManifestLabelsEnabled?: boolean;
      debug?: boolean;
    } = {},
    experimentsAPI?: ExperimentsAPI,
  ) {
    this.#experimentsAPI = experimentsAPI;
    this.debug =
      this.flags?.debug ??
      (typeof window !== 'undefined' &&
        typeof window.location !== 'undefined' &&
        new URLSearchParams(window.location.search).has('debug'));
  }

  get isPlatformCacheDisabled() {
    return !!this.flags.isPlatformCacheDisabled;
  }

  get isVirtualManifestAPICacheEnabled() {
    return !!this.flags.isVirtualManifestAPICacheEnabled;
  }

  get isManifestDynamicOverridesCacheEnabled() {
    return !!this.flags.isManifestDynamicOverridesCacheEnabled;
  }

  get isVirtualManifestBuildCacheEnabled() {
    return !!this.flags.isVirtualManifestBuildCacheEnabled;
  }

  get shouldMergeSlotManifests() {
    return !!this.flags.shouldMergeSlotManifests;
  }

  get shouldUseBuilderSlotsAPI() {
    return !!this.flags.shouldUseBuilderSlotsAPI;
  }

  get isRuntimeManifestResolutionEnabled() {
    return !!this.flags.isRuntimeManifestResolutionEnabled;
  }

  get isPersistentResolvedManifestCacheEnabled() {
    return !!this.flags.isPersistentResolvedManifestCacheEnabled;
  }

  get isLoadManifestWithoutDuplicationsEnabled() {
    return !!this.flags.isLoadManifestWithoutDuplicationsEnabled;
  }

  get isRuntimeManifestOverridesResolutionEnabled() {
    return !!this.flags.isRuntimeManifestOverridesResolutionEnabled;
  }

  get isComponentSpecFilteringEnabled() {
    return !!this.flags.isComponentSpecFilteringEnabled;
  }

  get isRuntimeManifestLabelsEnabled() {
    return !!this.flags.isRuntimeManifestLabelsEnabled;
  }

  isSpecEnabled(spec: string): boolean {
    return this.#experimentsAPI?.enabled(spec) ?? false;
  }
}
